import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { PageHero } from "../components/Shared";
import * as Icons from "lucide-react";

export default function Programme() {
  const [proposals, setProposals] = useState([]);
  const [domains, setDomains] = useState([]);
  const [showDomains, setShowDomains] = useState(false);
  const domainsRef = useRef(null);

  useEffect(() => {
    api.get("/proposals").then((r) => setProposals(r.data)).catch(() => {});
    api.get("/domains").then((r) => setDomains(r.data)).catch(() => {});
  }, []);

  const revealDomains = () => {
    setShowDomains(true);
    setTimeout(() => domainsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  return (
    <div data-testid="programme-page">
      <PageHero
        label="Nos trois priorités"
        title="Trois priorités pour l'Alsace"
        subtitle="Des propositions concrètes, construites avec les élus et les habitants, pour défendre l'Alsace au Sénat."
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid gap-8 lg:grid-cols-3 items-start">
          {proposals.map((p, i) => {
            const Icon = Icons[p.icon] || Icons.Flag;
            return (
              <article
                key={p.id}
                data-testid={`proposal-card-${i}`}
                className="group bg-white rounded-2xl p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200/70 hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="w-14 h-14 rounded-xl bg-[#12495B] text-[#FDFBF7] flex items-center justify-center">
                    <Icon size={26} />
                  </span>
                  <span className="font-display text-5xl text-[#12495B]/10 leading-none">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl text-[#12495B] leading-tight">{p.theme}</h3>
                <p className="text-[#12495B] font-semibold mt-3 leading-relaxed">{p.summary}</p>
                {p.detail && <p className="text-[#4B5563] mt-3 leading-relaxed">{p.detail}</p>}
                <ul className="mt-6 space-y-3 border-t border-slate-200/70 pt-6">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-[#111827]">
                      <Icons.Check size={18} className="text-[#F5C518] mt-0.5 shrink-0" strokeWidth={3} />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {!showDomains && (
          <div className="mt-16 text-center">
            <button
              onClick={revealDomains}
              data-testid="show-domains-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#12495B] text-white font-extrabold hover:bg-[#0E3B49] transition-colors duration-300 shadow-[0_10px_30px_rgb(18,73,91,0.25)]"
            >
              Nous proposons également <Icons.ChevronDown size={20} />
            </button>
          </div>
        )}
      </section>

      {showDomains && (
        <section
          ref={domainsRef}
          data-testid="domains-section"
          className="bg-white border-t border-slate-200/70"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="max-w-3xl mx-auto text-center py-10">
            <span className="text-xs tracking-[0.28em] uppercase font-extrabold text-[#12495B]/70">Nous proposons également</span>
            <p className="mt-6 font-display text-3xl sm:text-4xl leading-snug text-[#12495B]" data-testid="programme-detail-message">
              Notre équipe présentera en cours de campagne un programme détaillé pour défendre l'Alsace au Sénat.
            </p>
            <div className="mt-8 w-16 h-1 bg-[#F5C518] mx-auto rounded-full" />
          </div>
        </div>
        </section>
      )}
    </div>
  );
}
